import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../api/client'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface Candidate {
  PK?: string
  candidate_id?: string
  name?: string
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  origin?: string
  promoter?: string
  owner?: string
  created_at?: string
  reconciled_at?: string
  status?: string
  lead_status?: string
  contacted?: boolean
  distrito?: string
  dni?: string
  evaluacion?: string
  estado_gestion?: string
  motivo_rechazo?: string
}

interface CandidatesResponse {
  candidates: Candidate[]
  count: number
  next_key?: string
}

const LEAD_STATUS_ES: Record<string, string> = {
  NEW:                         'Nuevo',
  OPEN:                        'Abierto',
  IN_PROGRESS:                 'En progreso',
  CONNECTED:                   'Conectado',
  UNFINAL_LEAD:                'Lead no final',
  BAD_TIMING:                  'Mal momento',
  NO_CONTACT:                  'Sin contacto',
  OPEN_DEAL:                   'Deal abierto',
  OPEN_SALES:                  'Venta abierta',
  UNQUALIFIED:                 'No califica',
  NO_SALES:                    'Sin venta',
  DOES_NOT_ACCEPT_OPPORTUNITY: 'No acepta',
}

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  NEW:                        { bg: 'var(--c-info-light)', color: 'var(--c-info-ink)' },
  OPEN:                       { bg: 'var(--c-info-light)', color: 'var(--c-info-ink)' },
  IN_PROGRESS:                { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  CONNECTED:                  { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  UNFINAL_LEAD:               { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  BAD_TIMING:                 { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  NO_CONTACT:                 { bg: 'var(--c-brand-light)', color: 'var(--c-brand-hover)' },
  OPEN_DEAL:                  { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)' },
  OPEN_SALES:                 { bg: 'var(--c-warn-light)',  color: 'var(--c-warn)' },
  UNQUALIFIED:                { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' },
  NO_SALES:                   { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' },
  DOES_NOT_ACCEPT_OPPORTUNITY:{ bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' },
}

const DEFAULT_STATUS_STYLE = { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)' }

function candidateName(c: Candidate) {
  if (c.first_name || c.last_name)
    return `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
  return c.name ?? c.email ?? '—'
}

function candidateStatus(c: Candidate) {
  return c.lead_status ?? c.status ?? ''
}

function fmtDate(ts?: string) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtPhone(phone?: string) {
  if (!phone) return '—'
  const digits = phone.replace(/^51/, '')
  return `+51 ${digits.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`
}

function StatusBadge({ status }: { status: string }) {
  const st = STATUS_STYLES[status] ?? DEFAULT_STATUS_STYLE
  const label = LEAD_STATUS_ES[status] ?? status
  return (
    <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                   background: st.bg, color: st.color, whiteSpace: 'nowrap', display: 'inline-block' }}>
      {label || '—'}
    </span>
  )
}

// ── ViewToggle ────────────────────────────────────────────────────────────────

function ViewToggle({ expanded, onChange }: { expanded: boolean; onChange: (v: boolean) => void }) {
  const btn = (active: boolean, label: string, icon: string, onClick: () => void) => (
    <button onClick={onClick} style={{
      all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
      padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
      background: active ? 'white' : 'transparent',
      color: active ? 'var(--c-ink-soft)' : 'var(--c-ink-muted)',
      boxShadow: active ? '0 1px 3px rgb(13 21 32 / 0.12)' : 'none',
      transition: 'all 0.15s',
    }}>
      <span style={{ fontSize: '14px' }}>{icon}</span> {label}
    </button>
  )
  return (
    <div style={{ display: 'flex', background: 'var(--c-surface-faint)', borderRadius: '10px',
                  padding: '3px', gap: '2px' }}>
      {btn(!expanded, 'Compacta', '▤', () => onChange(false))}
      {btn(expanded,  'Completa', '⊞', () => onChange(true))}
    </div>
  )
}

// ── Drawer ────────────────────────────────────────────────────────────────────

function CandidateDrawer({ candidate, onClose }: { candidate: Candidate; onClose: () => void }) {
  const name = candidateName(candidate)
  const status = candidateStatus(candidate)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgb(13 21 32 / 0.35)',
                  zIndex: 20, display: 'flex', justifyContent: 'flex-end' }}
         onClick={onClose}>
      <div style={{ width: '420px', height: '100%', background: 'white', padding: '24px',
                    boxSizing: 'border-box', overflowY: 'auto', display: 'flex',
                    flexDirection: 'column', gap: '16px' }}
           onClick={(e) => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 800 }}>{name}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--c-ink-muted)', fontFamily: "'IBM Plex Mono', monospace" }}>
              {fmtPhone(candidate.phone)}
            </div>
          </div>
          <button onClick={onClose}
            style={{ all: 'unset', cursor: 'pointer', fontSize: '18px', color: 'var(--c-ink-muted)' }}>✕</button>
        </div>

        <StatusBadge status={status} />

        <div style={{ height: '1px', background: 'var(--c-surface-border)' }} />

        <div style={{ fontSize: '12.5px', lineHeight: 2 }}>
          {candidate.dni            && <div><b>DNI:</b> {candidate.dni}</div>}
          {candidate.email          && <div><b>Email:</b> {candidate.email}</div>}
          {candidate.distrito       && <div><b>Distrito:</b> {candidate.distrito}</div>}
          {candidate.promoter       && <div><b>Promotor:</b> {candidate.promoter}</div>}
          {candidate.owner          && <div><b>Propietario:</b> {candidate.owner}</div>}
          {candidate.origin         && <div><b>Origen:</b> {candidate.origin}</div>}
          {candidate.estado_gestion && <div><b>Estado gestión:</b> {candidate.estado_gestion}</div>}
          {candidate.evaluacion     && <div><b>Evaluación:</b> {candidate.evaluacion}</div>}
          {candidate.motivo_rechazo && <div><b>Motivo rechazo:</b> {candidate.motivo_rechazo}</div>}
          <div><b>Fecha lead:</b> {fmtDate(candidate.created_at ?? candidate.reconciled_at)}</div>
          <div><b>Contactado:</b> {candidate.contacted ? 'Sí' : 'No'}</div>
        </div>

        <div style={{ height: '1px', background: 'var(--c-surface-border)' }} />

        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--c-ink-muted)' }}>
          ACTIVIDAD EN HUBSPOT (SOLO LECTURA)
        </div>
        <div style={{ fontSize: '12px', padding: '10px', background: 'var(--c-surface-subtle)', borderRadius: '8px' }}>
          <div style={{ fontWeight: 600 }}>Lead sincronizado</div>
          <div style={{ color: 'var(--c-ink-muted)', fontSize: '11px', marginTop: '2px' }}>
            {fmtDate(candidate.created_at ?? candidate.reconciled_at)}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Vista compacta ────────────────────────────────────────────────────────────

const COMPACT_GRID = '1.4fr 1fr 1fr 1fr 0.9fr 1fr 0.7fr'

function CompactTable({ rows, onSelect }: { rows: Candidate[]; onSelect: (c: Candidate) => void }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: COMPACT_GRID,
                    padding: '12px 20px', fontSize: '11px', fontWeight: 700,
                    letterSpacing: '0.04em', color: 'var(--c-ink-muted)',
                    background: 'var(--c-surface-subtle)' }}>
        <span>NOMBRE</span><span>TELÉFONO</span><span>PROMOTOR</span>
        <span>PROPIETARIO</span><span>FECHA LEAD</span><span>ESTADO</span><span>CONTACTADO</span>
      </div>
      {rows.map((c, i) => {
        const id = c.candidate_id ?? c.PK ?? String(i)
        return (
          <div key={id} onClick={() => onSelect(c)}
            style={{ display: 'grid', gridTemplateColumns: COMPACT_GRID,
                     padding: '14px 20px', fontSize: '12.5px', alignItems: 'center',
                     borderTop: '1px solid var(--c-surface-faint)', cursor: 'pointer' }}
            className="hover:bg-surface-subtle transition-colors">
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {candidateName(c)}
            </span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: 'var(--c-ink-muted)' }}>
              {fmtPhone(c.phone)}
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.promoter ?? '—'}
            </span>
            <span style={{ color: 'var(--c-ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.owner ?? '—'}
            </span>
            <span style={{ color: 'var(--c-ink-muted)' }}>
              {fmtDate(c.created_at ?? c.reconciled_at)}
            </span>
            <StatusBadge status={candidateStatus(c)} />
            <span style={{ fontSize: '10.5px', fontWeight: 600, color: 'var(--c-ink-muted)' }}>
              {c.contacted ? 'Sí' : 'No'}
            </span>
          </div>
        )
      })}
    </>
  )
}

// ── Vista expandida ───────────────────────────────────────────────────────────

const EXP_COLS: Array<{ label: string; width: number }> = [
  { label: 'NOMBRE',         width: 200 },
  { label: 'DNI',            width: 90  },
  { label: 'TELÉFONO',       width: 130 },
  { label: 'EMAIL',          width: 190 },
  { label: 'DISTRITO',       width: 110 },
  { label: 'PROMOTOR',       width: 130 },
  { label: 'PROPIETARIO',    width: 160 },
  { label: 'FECHA LEAD',     width: 110 },
  { label: 'ESTADO GESTIÓN', width: 180 },
  { label: 'EVALUACIÓN',     width: 110 },
  { label: 'ESTADO',         width: 120 },
  { label: 'CONTACTADO',     width: 90  },
]

function ExpandedTable({ rows, onSelect }: { rows: Candidate[]; onSelect: (c: Candidate) => void }) {
  const totalWidth = EXP_COLS.reduce((a, c) => a + c.width, 0)
  const cell: React.CSSProperties = {
    display: 'flex', alignItems: 'center', padding: '0 12px',
    fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0,
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: totalWidth + 'px' }}>
        {/* Header */}
        <div style={{ display: 'flex', height: '36px', background: 'var(--c-surface-subtle)',
                      borderBottom: '1px solid var(--c-surface-border)' }}>
          {EXP_COLS.map(col => (
            <div key={col.label} style={{ ...cell, width: col.width + 'px', minWidth: col.width + 'px',
                                          fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.04em',
                                          color: 'var(--c-ink-muted)' }}>
              {col.label}
            </div>
          ))}
        </div>
        {/* Rows */}
        {rows.map((c, i) => {
          const id = c.candidate_id ?? c.PK ?? String(i)
          return (
            <div key={id} onClick={() => onSelect(c)}
              style={{ display: 'flex', height: '42px', cursor: 'pointer',
                       borderBottom: '1px solid var(--c-surface-faint)',
                       background: i % 2 === 0 ? 'white' : 'var(--c-surface-alt)' }}
              className="hover:bg-surface-subtle transition-colors">
              <div style={{ ...cell, width: '200px', minWidth: '200px', fontWeight: 600 }}>{candidateName(c)}</div>
              <div style={{ ...cell, width: '90px',  minWidth: '90px',  fontFamily: "'IBM Plex Mono', monospace", color: 'var(--c-ink-muted)' }}>{c.dni || '—'}</div>
              <div style={{ ...cell, width: '130px', minWidth: '130px', fontFamily: "'IBM Plex Mono', monospace", color: 'var(--c-ink-muted)' }}>{fmtPhone(c.phone)}</div>
              <div style={{ ...cell, width: '190px', minWidth: '190px', color: 'var(--c-ink-muted)' }}>{c.email || '—'}</div>
              <div style={{ ...cell, width: '110px', minWidth: '110px', color: 'var(--c-ink-muted)' }}>{c.distrito || '—'}</div>
              <div style={{ ...cell, width: '130px', minWidth: '130px' }}>{c.promoter || '—'}</div>
              <div style={{ ...cell, width: '160px', minWidth: '160px', color: 'var(--c-ink-muted)' }}>{c.owner || '—'}</div>
              <div style={{ ...cell, width: '110px', minWidth: '110px', color: 'var(--c-ink-muted)' }}>{fmtDate(c.created_at ?? c.reconciled_at)}</div>
              <div style={{ ...cell, width: '180px', minWidth: '180px', color: 'var(--c-ink-muted)' }}>{c.estado_gestion || '—'}</div>
              <div style={{ ...cell, width: '110px', minWidth: '110px', color: 'var(--c-ink-muted)' }}>{c.evaluacion || '—'}</div>
              <div style={{ ...cell, width: '120px', minWidth: '120px' }}><StatusBadge status={candidateStatus(c)} /></div>
              <div style={{ ...cell, width: '90px',  minWidth: '90px',  fontSize: '10.5px', fontWeight: 600, color: 'var(--c-ink-muted)' }}>{c.contacted ? 'Sí' : 'No'}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Candidates() {
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Candidate | null>(null)
  const [nextKey, setNextKey] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  const fetchCandidates = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await apiClient.get<CandidatesResponse>('/candidates?limit=100')
      setCandidates(r.data.candidates ?? [])
      setNextKey(r.data.next_key ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar candidatos')
    } finally { setLoading(false) }
  }, [])

  const loadMore = useCallback(async () => {
    if (!nextKey || loadingMore) return
    setLoadingMore(true)
    try {
      const r = await apiClient.get<CandidatesResponse>(`/candidates?limit=100&last_key=${encodeURIComponent(nextKey)}`)
      setCandidates(prev => [...prev, ...(r.data.candidates ?? [])])
      setNextKey(r.data.next_key ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar más candidatos')
    } finally { setLoadingMore(false) }
  }, [nextKey, loadingMore])

  useEffect(() => { void fetchCandidates() }, [fetchCandidates])

  const filtered = useMemo(() => {
    if (!search.trim()) return candidates
    const q = search.toLowerCase()
    return candidates.filter((c) =>
      [candidateName(c), c.email, c.phone, c.origin, c.promoter, c.dni, c.distrito, c.owner]
        .some((v) => v?.toLowerCase().includes(q))
    )
  }, [candidates, search])

  if (loading) return <LoadingSpinner label="Cargando candidatos..." />
  if (error)   return <ErrorAlert message={error} onRetry={fetchCandidates} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* Banner solo lectura */}
      <div style={{ background: 'var(--c-warn-light)', border: '1px solid var(--c-warn-border)',
                    borderRadius: '12px', padding: '12px 16px',
                    fontSize: '12.5px', color: 'var(--c-warn)' }}>
        HubSpot está conectado en modo <b>solo lectura</b>. No existe ninguna vía de escritura habilitada durante el MVP.
      </div>

      {/* Search + toggle */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="search" placeholder="Buscar por nombre, email, teléfono, DNI, distrito u origen..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="input" style={{ maxWidth: '420px', flex: 1 }}
        />
        <ViewToggle expanded={expanded} onChange={setExpanded} />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="👥" title={search ? 'Sin resultados' : 'Sin candidatos'}
          description={search ? `No coincide con "${search}".` : 'Los candidatos sincronizados desde HubSpot aparecerán aquí.'} />
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                      borderRadius: '16px', overflow: 'hidden' }}>
          {expanded
            ? <ExpandedTable rows={filtered} onSelect={setSelected} />
            : <CompactTable  rows={filtered} onSelect={setSelected} />
          }

          {/* Footer */}
          <div style={{ borderTop: '1px solid var(--c-surface-faint)', padding: '10px 20px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '11.5px', color: 'var(--c-ink-muted)',
                        background: 'var(--c-surface-subtle)' }}>
            <span>{filtered.length} de {candidates.length} candidatos cargados</span>
            {nextKey && !search && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                style={{ all: 'unset', cursor: loadingMore ? 'default' : 'pointer',
                         fontSize: '11.5px', fontWeight: 700, color: 'var(--c-info-ink)',
                         padding: '4px 12px', borderRadius: '8px',
                         background: 'var(--c-info-light)', opacity: loadingMore ? 0.6 : 1 }}>
                {loadingMore ? 'Cargando...' : 'Cargar más →'}
              </button>
            )}
          </div>
        </div>
      )}

      {selected && <CandidateDrawer candidate={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
